import React, { useEffect, useState, useRef } from 'react';
import { BasicModalWithFooter, TertiaryButton } from '@hz-design-system/web-ui';
import getIsMobile from '@/client/utils/browser/isMobile';
import Classes from './HighlightGuide.module.scss';

type Props = {
  targetRef: React.RefObject<HTMLElement | null>;
  show: boolean;
  title: string;
  description: string;
  onClose: () => void;
  updateTrigger?: number;
  doneText: string;
};

const ARROW_OFFSET = 6;

const HighlightGuide: React.FC<Props> = ({ targetRef, show, title, description, onClose, updateTrigger, doneText }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [modalSize, setModalSize] = useState({ width: 440, height: 190 });
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);

  const positionArrow = (
    arrowElement: HTMLElement,
    modalRect: DOMRect,
    targetRect: DOMRect,
    position: 'top' | 'bottom' | 'left' | 'right',
  ) => {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const modalCenterY = modalRect.top + modalRect.height / 2;

    arrowElement.className = Classes.arrow;

    switch (position) {
      case 'top':
        arrowElement.style.top = `${modalRect.top - ARROW_OFFSET}px`;
        arrowElement.style.left = `${targetCenterX - ARROW_OFFSET}px`;
        arrowElement.classList.add(Classes.arrowTop);
        break;
      case 'bottom':
        arrowElement.style.top = `${modalRect.bottom - ARROW_OFFSET}px`;
        arrowElement.style.left = `${targetCenterX - ARROW_OFFSET}px`;
        arrowElement.classList.add(Classes.arrowBottom);
        break;
      case 'left':
        arrowElement.style.top = `${modalCenterY - ARROW_OFFSET}px`;
        arrowElement.style.left = `${modalRect.left - ARROW_OFFSET}px`;
        arrowElement.classList.add(Classes.arrowLeft);
        break;
      case 'right':
        arrowElement.style.top = `${modalCenterY - ARROW_OFFSET}px`;
        arrowElement.style.left = `${modalRect.right - ARROW_OFFSET}px`;
        arrowElement.classList.add(Classes.arrowRight);
        break;
    }
  };

  const updatePositions = () => {
    if (!show || !viewportSize.width || !viewportSize.height) return;

    const parentElement = targetRef.current;
    if (!parentElement) return;

    const childElement = parentElement.children[0] as HTMLElement;
    if (!childElement) return;

    const newRect = childElement.getBoundingClientRect();
    setRect(newRect);
    const isMobile = getIsMobile();
    const arrowSize = 12;
    const padding = isMobile ? 12 : 16;

    let modalTop = 0;
    let modalLeft = 0;

    if (isMobile) {
      modalTop = newRect.bottom + arrowSize + padding;
      modalLeft = Math.max(padding, Math.min(newRect.left - 4, viewportSize.width - padding));

      if (modalTop > viewportSize.height - modalSize.height) {
        modalTop = newRect.top - modalSize.height - arrowSize - padding;
      }
    } else {
      modalTop = newRect.top;
      modalLeft = newRect.right + arrowSize + padding;

      if (modalLeft > viewportSize.width - modalSize.width) {
        modalLeft = newRect.left - modalSize.width - arrowSize - padding;
        if (modalLeft < 0) modalLeft = padding;
      }

      modalTop = newRect.top + newRect.height / 2 - modalSize.height / 2;
      if (modalTop < padding) modalTop = padding;
      if (modalTop > viewportSize.height - modalSize.height)
        modalTop = viewportSize.height - modalSize.height - padding;
    }

    setModalPosition({ top: modalTop, left: modalLeft });
  };

  useEffect(() => {
    const updateViewportSize = () => {
      setViewportSize({
        width: document.documentElement.clientWidth,
        height: document.documentElement.clientHeight,
      });
    };

    updateViewportSize();

    const resizeObserver = new ResizeObserver(() => {
      updateViewportSize();
    });

    resizeObserver.observe(document.documentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const parentElement = targetRef.current;
    if (!parentElement) return;

    if (!show) {
      parentElement.classList.remove(Classes.targetHighlighted, Classes.targetMobile);
      parentElement.classList.add(Classes.targetNormal);
      return;
    }

    parentElement.classList.remove(Classes.targetNormal);
    parentElement.classList.add(Classes.targetHighlighted);

    const isMobile = getIsMobile();
    if (isMobile) {
      parentElement.classList.add(Classes.targetMobile);
    } else {
      parentElement.classList.remove(Classes.targetMobile);
    }

    updatePositions();

    const targetResizeObserver = new ResizeObserver(() => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(updatePositions, 10);
    });

    targetResizeObserver.observe(parentElement);

    return () => {
      targetResizeObserver.disconnect();
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      if (parentElement) {
        parentElement.classList.remove(Classes.targetHighlighted, Classes.targetMobile, Classes.targetNormal);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, show, updateTrigger, viewportSize]);

  useEffect(() => {
    if (!show || !rect) return;

    const positionModal = () => {
      const guideElement =
        (document.querySelector('.hz-Modal') as HTMLElement) ||
        (document.querySelector('[role="dialog"]') as HTMLElement);
      const arrowElement = arrowRef.current;

      if (guideElement) {
        guideElement.style.top = `${modalPosition.top}px`;
        guideElement.style.left = `${modalPosition.left}px`;
        guideElement.style.transform = 'none';
      }

      if (arrowElement && rect) {
        const modalRect = guideElement?.getBoundingClientRect();
        if (modalRect) {
          setModalSize({ width: modalRect.width, height: modalRect.height });

          const isMobile = getIsMobile();
          if (isMobile) {
            const arrowPosition = modalPosition.top > rect.bottom ? 'top' : 'bottom';
            positionArrow(arrowElement, modalRect, rect, arrowPosition);
          } else {
            const arrowPosition = modalPosition.left > rect.right ? 'left' : 'right';
            positionArrow(arrowElement, modalRect, rect, arrowPosition);
          }
        }
      }
    };

    const timeoutId = setTimeout(positionModal, 50);
    return () => clearTimeout(timeoutId);
  }, [show, rect, modalPosition]);

  if (!show || !rect) return null;

  const closeGuide = () => {
    onClose();
  };

  return (
    <div ref={containerRef}>
      <div ref={arrowRef} className={Classes.arrow} />

      <BasicModalWithFooter
        isOpen={show}
        title={title}
        onClose={closeGuide}
        size="s"
        className={Classes.highlightModal}
        buttonsAlignmentMobile="stretched"
        buttonsAlignmentDesktop="rightAligned"
        buttons={[
          {
            element: <TertiaryButton onClick={closeGuide}>{doneText}</TertiaryButton>,
          },
        ]}
      >
        {description}
      </BasicModalWithFooter>
    </div>
  );
};

export default HighlightGuide;
